import React, { FC, useState, useRef, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  Typography,
} from '@mui/material'
import { MedicationInsertButton } from '@/apps/legacy/components/molecules/MedicationInsertButton'
import { insertAtCursor } from '@/shared/utils/textFieldHelper'
import { sanitizeInput } from '@/shared/utils/sanitize'
import { Soaps, SoapSectionKeys } from '@/shared/types/yakurekiSoap'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'

// UI定数
const FOCUS_DELAY_MS = 100

interface BulkReplaceDialogProps {
  open: boolean
  onClose: () => void
  soapContent: Soaps | undefined
  medications: string[]
  onReplace: (searchText: string, replaceText: string) => Promise<void>
}

export const BulkReplaceDialog: FC<BulkReplaceDialogProps> = ({
  open,
  onClose,
  soapContent,
  medications,
  onReplace,
}) => {
  const [searchText, setSearchText] = useState('')
  const [replaceText, setReplaceText] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [matchCount, setMatchCount] = useState(0)

  const searchRef = useRef<HTMLInputElement>(null)
  const replaceRef = useRef<HTMLInputElement>(null)

  // ダイアログが開いた時にフォーカス
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        searchRef.current?.focus()
      }, FOCUS_DELAY_MS)
    }
  }, [open])

  // マッチ件数をカウント
  useEffect(() => {
    if (!searchText || !soapContent) {
      setMatchCount(0)
      return
    }

    let count = 0
    const searchLower = searchText.toLowerCase()

    // 全てのSOAP項目を検索（idフィールドを除外）
    Object.keys(soapContent).forEach((key) => {
      // idフィールドはスキップ
      if (key === 'id') return

      const typedKey = key as SoapSectionKeys
      const items = soapContent[typedKey]
      // itemsが配列であることを確認
      if (Array.isArray(items)) {
        items.forEach((item) => {
          if (
            item &&
            item.content &&
            item.content.toLowerCase().includes(searchLower)
          ) {
            // 文字列内の出現回数をカウント
            const matches =
              item.content.toLowerCase().split(searchLower).length - 1
            count += matches
          }
        })
      }
    })

    setMatchCount(count)
  }, [searchText, soapContent])

  const handleSearchInsert = (text: string) => {
    insertAtCursor(searchRef, searchText, setSearchText, text)
  }

  const handleReplaceInsert = (text: string) => {
    insertAtCursor(replaceRef, replaceText, setReplaceText, text)
  }

  const handleExecuteReplace = async () => {
    if (!searchText || replaceText === undefined) return

    // 入力値をサニタイズしてXSS攻撃を防ぐ
    const sanitizedSearchText = sanitizeInput(searchText)
    const sanitizedReplaceText = sanitizeInput(replaceText)

    setIsProcessing(true)
    try {
      await onReplace(sanitizedSearchText, sanitizedReplaceText)
      handleClose()
    } catch (_error) {
      // エラーハンドリングはonReplaceの呼び出し元で処理
    } finally {
      setIsProcessing(false)
    }
  }

  const handleClose = () => {
    if (!isProcessing) {
      setSearchText('')
      setReplaceText('')
      onClose()
    }
  }

  // Notion 流 dialog spec — icon 不使用、 dense border-bottom で分節、
  // accent-primary fill primary CTA + ghost secondary cancel.
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isProcessing}
      PaperProps={{ sx: { borderRadius: 1.5 } }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 1.5,
          borderBottom: '1px solid rgba(55,53,47,0.09)',
        }}
      >
        <Typography
          sx={{ fontSize: 14, fontWeight: 500, color: 'text.primary' }}
        >
          一括置換
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* 検索文字列 */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontSize: 11,
                fontWeight: 500,
                color: 'text.secondary',
                mb: 0.5,
              }}
            >
              検索文字列
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                ref={searchRef}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                fullWidth
                size="small"
                disabled={isProcessing}
                placeholder="置換前のテキスト"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: 14,
                    borderRadius: 1,
                    '& fieldset': { borderColor: 'rgba(55,53,47,0.16)' },
                  },
                }}
              />
              <MedicationInsertButton
                medications={medications}
                onInsert={handleSearchInsert}
                buttonSize="small"
                disabled={isProcessing}
              />
            </Box>
            {searchText && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  fontSize: 11,
                  color: matchCount > 0 ? 'text.secondary' : '#C29343',
                  mt: 0.5,
                }}
              >
                {matchCount > 0
                  ? `${matchCount} 件の置換対象`
                  : '置換対象が見つかりません'}
              </Typography>
            )}
          </Box>

          {/* 置換文字列 */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontSize: 11,
                fontWeight: 500,
                color: 'text.secondary',
                mb: 0.5,
              }}
            >
              置換文字列
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                ref={replaceRef}
                value={replaceText}
                onChange={(e) => setReplaceText(e.target.value)}
                fullWidth
                size="small"
                disabled={isProcessing}
                placeholder="置換後のテキスト (空欄で削除)"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: 14,
                    borderRadius: 1,
                    '& fieldset': { borderColor: 'rgba(55,53,47,0.16)' },
                  },
                }}
              />
              <MedicationInsertButton
                medications={medications}
                onInsert={handleReplaceInsert}
                buttonSize="small"
                disabled={isProcessing}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          borderTop: '1px solid rgba(55,53,47,0.09)',
          gap: 0.5,
        }}
      >
        <Button
          onClick={handleClose}
          disabled={isProcessing}
          size="small"
          sx={{
            textTransform: 'none',
            fontSize: 13,
            fontWeight: 500,
            color: 'text.secondary',
            borderRadius: 1,
            '&:hover': { bgcolor: 'rgba(55,53,47,0.06)' },
          }}
        >
          キャンセル
        </Button>
        <Button
          onClick={handleExecuteReplace}
          variant="contained"
          disabled={!searchText || matchCount === 0 || isProcessing}
          size="small"
          sx={{
            textTransform: 'none',
            fontSize: 13,
            fontWeight: 500,
            bgcolor: colorTokens.blue[600],
            color: 'background.default',
            borderRadius: 1,
            boxShadow: 'none',
            '&:hover': {
              bgcolor: colorTokens.blue[600],
              opacity: 0.9,
              boxShadow: 'none',
            },
          }}
        >
          {isProcessing ? '処理中…' : '置換を実行'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
